import { $insertList } from '@lexical/list';
import { $setBlocksType } from '@lexical/selection';
import { $getNearestNodeOfType } from '@lexical/utils';
import { $getRoot, $getSelection, $isRangeSelection, ElementNode, RangeSelection } from 'lexical';
import { BrandColorRef, findBrandColor } from 'modules/BrandDefinition';
import { $createCustomHeadingNode, $isCustomHeadingNode, CustomHeadingNode } from 'modules/Lexical/nodes/CustomHeadingNode';
import { $isCustomListItemNode, CustomListItemNode } from 'modules/Lexical/nodes/CustomListItemNode';
import { $createCustomParagraphNode, $isCustomParagraphNode, CustomParagraphNode } from 'modules/Lexical/nodes/CustomParagraphNode';
import { HeadingTagType } from 'const';
import { DefaultTextStyles } from 'const/Styles';
import * as Models from 'models';
import { colorExistsInBrandColors } from 'utils/brandStyles';
import { $isCustomListNode, CustomListNode } from '../../nodes/CustomListNode';
import { $hasLineHeight } from '../TextBlockPlugin/utils';

export const $isSelectionForListItem = (): boolean => {
  const selection = $getSelection();
  if (!$isRangeSelection(selection)) {
    return false;
  }

  return $isCustomListNode(selection.getNodes()[0].getTopLevelElement());
};

export const $getBulletColorForFirstList = (colors: Models.BrandColorsList): Models.BrandColorMap | undefined => {
  const selection = $getSelection();

  if (!selection) {
    return undefined;
  }

  const parents = selection.getNodes()[0]?.getParents();
  const customListNode = parents?.find(item => $isCustomListNode(item)) as unknown as CustomListNode | undefined;
  const nodeColor = customListNode?.getColor();

  return findBrandColor(colors, nodeColor);
};

export const $getTopLevelCustomListFromSelection = (): CustomListNode | undefined => {
  const selection = $getSelection();
  if (selection || $isRangeSelection(selection)) {
    const parent = selection.getNodes()[0].getTopLevelElement();
    if ($isCustomListNode(parent)) {
      return parent;
    }
  }

  return undefined;
};

export function $doAllNodesHaveBulletColorHex(hex: string): boolean {
  // only root list node contains bullet color value
  const nodes = $getRoot().getChildren().filter(node => $isCustomListNode(node));
  if (!nodes.length) {
    return false;
  }

  return nodes.every((node: CustomListNode) => {
    const hexColor = node.getColor()?.HEX ?? DefaultTextStyles.bulletColor;

    return hex === hexColor;
  });
}

export function $applyBulletColorToAllNodes(colorRef: BrandColorRef): void {
  // only root list node contains bullet color value
  $getRoot().getChildren()
    .filter(node => $isCustomListNode(node))
    .every((node: CustomListNode) => node.setColor(colorRef));
}

export const $matchBulletColor = (
  colors: Models.BrandColorsList,
  brandColorStyle: BrandColorRef,
): string | undefined => {
  let areSeveralColorsApplied = false;
  let commonColorName = '';

  const nodes = $getRoot().getChildren().filter(node => $isCustomListNode(node));

  nodes.forEach((node: CustomListNode, index) => {
    const currentColorName = node.getColor()?.name ?? '';

    if (currentColorName !== brandColorStyle.name) {
      const styleExists = colorExistsInBrandColors(currentColorName, colors);

      if (!styleExists) {
        node.setColor(brandColorStyle);
      }
    }

    // save first font color style for further detecting whether multiple font colors applied
    if (index === 0) {
      commonColorName = currentColorName;
    } else if (!areSeveralColorsApplied && currentColorName !== commonColorName) {
      areSeveralColorsApplied = true;
    }
  });

  return areSeveralColorsApplied ? undefined : commonColorName;
};

function $getNodesWithLineHeight(selection: RangeSelection): (CustomParagraphNode | CustomListItemNode | CustomHeadingNode)[] {
  const nodesWithLineHeight = selection.getNodes().filter($hasLineHeight);
  if (nodesWithLineHeight.length === 0) {
    const nearestParagraph = $getNearestNodeOfType(selection.anchor.getNode(), CustomParagraphNode);
    const nearestListItem = $getNearestNodeOfType(selection.anchor.getNode(), CustomListItemNode);
    const nearestHeadingNode = $getNearestNodeOfType(selection.anchor.getNode(), CustomHeadingNode);

    return [nearestParagraph, nearestListItem, nearestHeadingNode].filter($hasLineHeight);
  }

  return nodesWithLineHeight;
}

export function $insertCustomListAndKeepStyles(): void {
  const rangeSelection = $getSelection();
  if (!$isRangeSelection(rangeSelection)) {
    return;
  }
  // FEATURE UPDATE:
  // This approach (mapping nodes by index before and after inserting the list) turned out to be the simplest and quickest solution.
  // In the future, it might be worth considering a more reliable method of node matching
  const nodesBeforeInsert = $getNodesWithLineHeight(rangeSelection);
  $insertList('bullet');
  const newRangeSelection = $getSelection();
  if (!$isRangeSelection(newRangeSelection)) {
    return;
  }
  const nodesAfterInsert = $getNodesWithLineHeight(newRangeSelection);
  nodesAfterInsert.forEach((node, index) => {
    node.setLineHeight(nodesBeforeInsert[index]?.getLineHeight());
    node.setFontSize(nodesBeforeInsert[index]?.getFontSize());
  });
}

export function $removeCustomListAndKeepStyles(brandStyleHeadingTag?: HeadingTagType): void {
  const selection = $getSelection();
  $setBlocksType(
    selection,
    () => brandStyleHeadingTag ? $createCustomHeadingNode(brandStyleHeadingTag) : $createCustomParagraphNode(),
    (prevNodeSrc: ElementNode, newNodeDest: CustomParagraphNode | CustomHeadingNode) => {
      if (!$isCustomListItemNode(prevNodeSrc) && !$isCustomParagraphNode(prevNodeSrc) && !$isCustomHeadingNode(prevNodeSrc)) {
        return;
      }
      newNodeDest.setLineHeight(prevNodeSrc.getLineHeight());
      newNodeDest.setFontSize(prevNodeSrc.getFontSize());
    },
  );
}

